<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Button,
		ButtonGroup,
		Stack,
		Text,
		Select,
		ControlsContainer,
		Container,
	} from '@Shelly/shelly-design-system';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';

	//Components
	import TimePickerFormatWrapper from 'src/components/TimePicker/TimePickerFormatWrapper.svelte';
	//Helpres
	import { digitFormat } from 'src/helpers/date_helper';
	import { capFirstChar } from 'src/helpers/utilities';
	//Types
	import type { ScheduleParams } from 'src/types/requests/schedules/schedules_g2';
	import ModeSelection from './ModeSelection.svelte';
	import ScheduleCalendar from './ScheduleCalendar.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let schedule: ScheduleParams,
		isMobile = false,
		showOption = true,
		scrollPaddings = false;

	const hoursOptions = Array.from(
			{ length: 13 },
			(_, i: any) =>
				(i = {
					value: i,
					title: digitFormat(i) + $format('label_h_countdow', { default: 'h' }),
				}),
		),
		minsOptions = Array.from(
			{ length: 60 },
			(_, i: any) =>
				(i = {
					value: i,
					title: digitFormat(i) + $format('timer_text_min', { default: 'timer_text_min' }),
				}),
		);
	let options: SelectOptionPropsType[] = [
		{
			title: $format('schedule_opt_time', { default: 'Time' }),
			value: 'time',
		},
		{
			title: `${$format('schedule_opt_sunrise', {
				default: 'Sunrise',
			})} / ${$format('schedule_opt_sunset', { default: 'Sunset' })}`,
			value: 'sunrise',
		},
	];

	let prevScheduleType: ScheduleParams['type'] = schedule.type || 'time';

	const switchAdvancedMode = (isAdvancedModeTimeTab: boolean) => {
		if (isAdvancedModeTimeTab) {
			prevScheduleType = schedule.type;
			schedule.type = 'advancedTime';
		} else if (!isAdvancedModeTimeTab) {
			schedule.type = prevScheduleType == 'advancedTime' ? 'time' : prevScheduleType;
			schedule.hours.selected = false;
			schedule.minutes.selected = false;
			schedule.seconds.selected = false;
		}
	};

	let isAdvancedModeTimeTab = schedule.type == 'advancedTime';
	$: switchAdvancedMode(isAdvancedModeTimeTab);
</script>

{#if showOption}
	<ModeSelection
		{scrollPaddings}
		{isMobile}
		bind:activeButtonTab={isAdvancedModeTimeTab}
		infoText={$format('schedule_choose_time_mode_tooltip', {
			default:
				'Choose Simple mode to select specific time or intervals based on Sunrise/Sunset. Switch to Advanced mode for more complex schedules.',
		})}
	/>
{/if}

<Container padding={scrollPaddings ? 'scrollPaddings' : '0'}>
	{#if showOption}
		<hr class="mt-l mb-l" />
	{/if}
	{#if showOption && schedule.type != 'advancedTime'}
		<Text styleType="h2" class="mb-s"
			>{$format('choose_time_interval', {
				default: 'Choose time interval',
			})}</Text
		>
		<Select
			size="m"
			class="mb-m"
			value={options.find((i) => i.value == schedule.type) || options[1]}
			{options}
			icon="fas fa-clock"
			on:change={(e) => {
				schedule.type = e.detail['value'];
			}}
			id="select-nu0ws4l"
			aria-label={$format('choose_time_interval', {
				default: 'Choose time interval',
			})}
		/>
	{/if}
	{#if /^(sunrise|sunset)$/.test(schedule.type)}
		<ControlsContainer color={$showNewLayout ? 'color-0' : undefined}>
			<svelte:fragment slot="first">
				<div class="d-flex mb-m justify-content-center">
					<Select
						class="width-md-s width-sm-full mr-s"
						icon="fas fa-clock"
						options={hoursOptions}
						value={hoursOptions.find((i) => i.value == schedule.sunHour)}
						on:change={(e) => {
							schedule.sunHour = e.detail['value'];
							if (schedule.sunHour == 12) schedule.sunMin = 0;
						}}
						id="select-kuery8p"
						aria-label={$format('label_h_countdow', { default: 'h' })}
					/>
					<Select
						class="width-md-s width-sm-full"
						icon="fas fa-clock"
						disabled={schedule.sunHour == 12}
						options={minsOptions}
						value={minsOptions.find((i) => i.value == schedule.sunMin)}
						on:change={(e) => {
							schedule.sunMin = e.detail['value'];
						}}
						id="select-0s823ll"
						aria-label={$format('timer_text_min', {
							default: 'timer_text_min',
						})}
					/>
				</div>
				<div class="d-flex justify-content-center">
					<Stack gap="s" direction="column">
						<ButtonGroup
							id="buttongroup-c4zgnwi"
							class="text-capitalize"
							aria-label={$format('schedule_before_after', { default: 'Before / After' })}
						>
							<Button
								size="m"
								class={`text-capitalize ${schedule.inc == '-' ? 'active' : undefined}`}
								on:click={() => (schedule.inc = '-')}
								>{$format('text_before', { default: 'Before' })}</Button
							>
							<Button
								size="m"
								class={`text-capitalize ${schedule.inc == '+' ? 'active' : undefined}`}
								on:click={() => (schedule.inc = '+')}
								>{$format('text_after', { default: 'After' })}</Button
							>
						</ButtonGroup>
						<ButtonGroup
							id="buttongroup-m788aps"
							aria-label={$format('schedule_sunrise_sunset', {
								default: 'Sunrise / Sunset',
							})}
						>
							<Button
								size="m"
								class={`text-capitalize ${schedule.type == 'sunrise' ? 'active' : undefined}`}
								on:click={() => (schedule.type = 'sunrise')}
								>{$format('schedule_opt_sunrise')}</Button
							>
							<Button
								size="m"
								class={`text-capitalize ${schedule.type == 'sunset' ? 'active' : undefined}`}
								on:click={() => (schedule.type = 'sunset')}
								>{$format('schedule_opt_sunset')}</Button
							>
						</ButtonGroup>
					</Stack>
				</div>
			</svelte:fragment>
		</ControlsContainer>
	{:else if schedule.type == 'advancedTime'}
		<ScheduleCalendar
			type="hours"
			bind:schedule
			{isMobile}
			toggleText={capFirstChar($format('select_units_hours', { default: 'hours' }))}
		/>
		<hr class="mt-l mb-l" />
		<ScheduleCalendar
			type="minutes"
			bind:schedule
			{isMobile}
			toggleText={capFirstChar($format('select_units_minutes', { default: 'minutes' }))}
		/>
		<hr class="mt-l mb-l" />
		<ScheduleCalendar
			type="seconds"
			bind:schedule
			{isMobile}
			toggleText={capFirstChar($format('select_units_seconds', { default: 'seconds' }))}
		/>
	{:else}
		<ControlsContainer color={$showNewLayout ? 'color-0' : undefined}>
			<svelte:fragment slot="first">
				<TimePickerFormatWrapper
					hours={schedule.hour}
					mins={schedule.min}
					on:change={(e) => {
						schedule.hour = e.detail['hours'];
						schedule.min = e.detail['mins'];
					}}
				/>
			</svelte:fragment>
		</ControlsContainer>
	{/if}
</Container>
